import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getTicketsByEventId } from '../../../app/store/slices/ticket/ticket';
import { getAllGstRates } from '../../../app/store/slices/gst-rates/gst';
import {
    getEventBySlug,
    getEventScheduleById,
    setEventData,
    setActiveTabAndMaxTabLimit
} from '../../../app/store/slices/event/event';
import { getAllCheckInStaff } from '../../../app/store/slices/event/team/team';
import {
    getCategories,
    getSubCategories,
    getFormats,
    getTags
} from '../../../app/store/slices/event/eventSetup';
import { getAllInvitations } from '../../../app/store/slices/event/invitation';
import { getUserInterestedEventsByUserId } from '../../../app/store/slices/event/event';
import { getAttendeeRegistrations } from '../../../app/store/slices/event/registration/registration';
import { getAllCities, getAllStates, getAllCountries } from '../../../app/store/slices/cities';
import { useEventStatus } from '../../../hooks/event-hooks/useEventStatus';
import useEvent from '../../../hooks/event-hooks/useEvent';

export default function useEventDashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { eventSlug } = useParams();

    const { user, isLoggedIn } = useSelector((state) => state.auth);
    const { eventData } = useSelector((state) => state.event);
    const { gstRates } = useSelector((state) => state.gst);
    const { tickets } = useSelector((state) => state.ticket);
    const { subCategories, categories, formats, tags } = useSelector((state) => state.eventSetup);
    const { cities, states, countries } = useSelector((state) => state.cities);

    const [loading, setLoading] = useState(true);
    const [isEventFetchProcessComplete, setIsEventFetchProcessComplete] = useState(false);

    const { isScheduleTabCompleted } = useEventStatus();

    const {
        isEventPublishedOnce
    } = useEvent();

    // variable to handle new event creation
    const isNewEvent = useMemo(() => eventSlug === 'new', [eventSlug]);

    // Check that user has access to the event
    const userHasAccess = useMemo(() => {
        const userId = user?.id;
        const userIdInEvent = eventData?.user_id === userId;
        const isEventSlugMatch = eventData?.slug === eventSlug;

        return userIdInEvent && isEventSlugMatch;
    }, [eventData?.slug, eventData?.user_id, eventSlug, user?.id]);

    // Check if the event is exists or not and user has access
    // const isEventFound = useMemo(() => {
    //     return eventData && eventData.slug === eventSlug && userHasAccess;
    // }, [eventData, eventSlug, userHasAccess]);

    // Treat new event as "found" so UI renders create form
    const isEventFound = useMemo(() => {
        if (isNewEvent) return true;
        return !!(eventData && eventData.slug === eventSlug && userHasAccess);
    }, [eventData, eventSlug, userHasAccess, isNewEvent]);

    // fetch the event based on certain conditions
    useEffect(() => {
        const fetchEventData = async () => {
            // Do not proceed further if eventSlug, user Id and isNewEvent are not valid
            // if (!eventSlug || !user?.id || isNewEvent) {
            //     dispatch(setEventData(null));
            //     dispatch(setActiveTabAndMaxTabLimit());
            //     setLoading(false);
            //     return;
            // }

            // Wait for user id to be available
            if (!user?.id || !isLoggedIn) {
                setLoading(true);

                // Navigate to homepage if user is not logged in for tempporary safety
                navigate('/');
                return;
            }

            // Handle new event case
            if (!eventSlug || isNewEvent) {
                dispatch(setEventData(null));
                dispatch(setActiveTabAndMaxTabLimit());
                setLoading(false);
                setIsEventFetchProcessComplete(true);
                return;
            }

            // Reset fetch process complete status
            setLoading(true);
            setIsEventFetchProcessComplete(false);

            // If already loaded, just stop loading
            if (eventData && eventData.slug === eventSlug) {
                setLoading(false);
                setIsEventFetchProcessComplete(true);
                return;
            }

            // Fresh load → show loader
            // setLoading(true);
            try {
                await dispatch(getEventBySlug(eventSlug)).unwrap();
                setLoading(false);
            } catch (error) {
                setLoading(false);
            } finally {
                setLoading(false);
                setIsEventFetchProcessComplete(true);
            }
        };

        fetchEventData();
    }, [dispatch, eventSlug, user?.id, eventData?.status, eventData?.publish, isNewEvent]);

    // Fetch event again if changes are detects in dependencies
    // useEffect(() => {
    //     if (eventData?.eventId && user?.id && tickets?.length) {
    //         dispatch(getEventBySlug(eventSlug));
    //     }
    // }, [user?.id, tickets?.length, eventSlug]);

    // Fetch event again if changes are detects in dependencies
    useEffect(() => {
        // Avoid refetching on "new" route or when slug is missing/mismatched
        if (!eventSlug || isNewEvent) return;
        if (eventData?.eventId && user?.id && tickets?.length && eventData?.slug === eventSlug) {
            dispatch(getEventBySlug(eventSlug));
        }
    }, [eventSlug, isNewEvent, eventData?.eventId, eventData?.slug, user?.id, tickets?.length]);

    // load data on specific criteria
    useEffect(() => {
        if (isNewEvent || !user?.id || !eventData?.eventId) return;

        // Tickets → only if schedule tab completed
        if (isScheduleTabCompleted) {
            dispatch(getTicketsByEventId(eventData.eventId));
        }

        // Schedule → only if schedule tab completed AND has date/time
        const hasDateOrTime =
            eventData?.start_date ||
            eventData?.end_date ||
            eventData?.start_time ||
            eventData?.end_time;

        if (isScheduleTabCompleted && hasDateOrTime) {
            dispatch(getEventScheduleById(eventData.eventId));
        }
    }, [
        eventData?.eventId,
        eventData?.start_date,
        eventData?.end_date,
        eventData?.start_time,
        eventData?.end_time,
        isScheduleTabCompleted,
        user?.id,
        isNewEvent
    ]);

    // Event setup data fetching (No matter if new event or existing)
    useEffect(() => {
        if (!user?.id || !isLoggedIn) return;

        const fetchEventSetupData = async () => {
            const promises = [];

            if (!categories?.length) promises.push(dispatch(getCategories()));
            if (!formats?.length) promises.push(dispatch(getFormats()));
            if (!subCategories?.length) promises.push(dispatch(getSubCategories()));
            if (!tags?.length) promises.push(dispatch(getTags()));
            if (!gstRates?.length) promises.push(dispatch(getAllGstRates()));
            if (!cities?.length) promises.push(dispatch(getAllCities()));
            if (!states?.length) promises.push(dispatch(getAllStates()));
            if (!countries?.length) promises.push(dispatch(getAllCountries()));

            if (promises.length > 0) {
                try {
                    await Promise.all(promises);
                } catch (error) {
                    console.error('Failed to fetch setup data:', error);
                }
            }
        };

        fetchEventSetupData();
    }, [user?.id, isLoggedIn]);


    useEffect(() => {
        if (isEventPublishedOnce && eventData?.eventId) {
            dispatch(getAllCheckInStaff({ eventId: eventData?.eventId, type: 'staff' }));
            dispatch(getAttendeeRegistrations(eventData?.eventId));
            dispatch(getAllInvitations(eventData?.eventId));
            dispatch(getUserInterestedEventsByUserId(user?.id));
        }
    }, [isEventPublishedOnce])

    return {
        loading,
        isEventFound,
        isEventFetchProcessComplete,
        isNewEvent
    };
}